"use client"

/**
 * The builder's plan card, as Eugene laid it out in Figma (Landing
 * template, 492:11963, 30.09), laid out as the homepage's baseline card is
 * now. On the left the heading, the line under it, the price, two buttons,
 * booking and "Learn more", and a line on cancelling. The price is big as on
 * the homepage, with twice and four times a year in small type under it;
 * Figma's price table went, the rhythms are in the popup (Eugene, 30.09).
 * The plan's three promises and its lines sit on the right. "Learn more"
 * opens the popup with every benefit, the rhythms and the 80+ markers. The
 * line on cancelling and a link to a one-time check-up sit under the card.
 *
 * The promises and the lines are the membership page's (Eugene, 30.09:
 * "take the real benefits from the membership page"), in pricingV5.
 *
 * The card is 1120 wide, a little wider than the lists below it, with two
 * even columns (Eugene, 30.09: first "the add-ons' width", then "wider"). The background,
 * icons and highlight are the homepage card's (../home/home-v4-baseline.tsx). That card
 * itself is not imported, as it carries the MRI switch and its price roll.
 * Figma's own ground is the older navy photograph. The card keeps the
 * coast one the homepage uses now.
 */

import Image from "next/image"
import { useState, type ReactNode } from "react"
import { Button } from "@/components/ui/button"
import { CheckCircleNeutralIcon } from "@/components/ui/icons"
import { blobMediaUrl } from "@/lib/blob-url"
import { cn } from "@/lib/utils"
import type { BookingScan } from "../home/home-v4-data"
import { HomeV4BaselineBackground } from "../home/home-v4-baseline-background"
import {
  PercentSealIcon,
  ShieldPlusIcon,
  StethoscopeFigmaIcon,
  TargetIcon,
} from "../home/home-v4-figma"
import {
  InfoSheet,
  PlanMark,
  SectionHeading,
  useInfoContent,
} from "./pricing-v5-booking"
import { usePricingV5Copy } from "./pricing-v5-copy"
import { PLAN_TEST } from "./pricing-v5-items"
import {
  BookButton,
  OneTimeLink,
  usePlanPrices,
  type Copy,
} from "./pricing-v5-shared"

/** The promises' icons, in Figma's order. */
const PROMISE_ICONS = [ShieldPlusIcon, TargetIcon, StethoscopeFigmaIcon]

/** Of the card's lines, the third, plan prices, is lit as on the homepage. */
const PLAN_PRICES_LINE = 2

/** How many of the plan's lines the card shows. The popup shows them all. */
const CARD_LINES = 5

/** How often, in the plan's popup: the three rhythms and what they add. */
function PlanRhythms({ PLAN }: { PLAN: Copy["PLAN"] }) {
  const { money, tier: tierAmount } = usePlanPrices()
  return (
    <div className="flex flex-col gap-1">
      <SectionHeading>{PLAN.rhythmLabel}</SectionHeading>
      <ul className="flex flex-col gap-0.5 overflow-hidden rounded-2xl">
        {PLAN.tiers.map((tier) => (
          <li
            key={tier.name}
            className="flex items-start justify-between gap-6 bg-neutral-50 px-6 py-4"
          >
            <div className="min-w-0">
              <p className="type-title-md text-neutral-900">{tier.name}</p>
              <p className="type-body-md mt-0.5 text-neutral-600">
                {tier.desc}
              </p>
            </div>
            <p className="type-title-md whitespace-nowrap text-neutral-900">
              {money(tierAmount(tier.id))}
            </p>
          </li>
        ))}
      </ul>
      <p className="type-body-md pt-2 text-neutral-500">{PLAN.samePrice}</p>
    </div>
  )
}

/**
 * The plan's price as the homepage card sets it, big, with its period, and
 * twice and four times a year in small type under it (Eugene, 30.09). What
 * each rhythm includes is in the popup behind "Learn more".
 */
function YearPrice({
  PLAN,
  className,
}: {
  PLAN: Copy["PLAN"]
  className?: string
}) {
  const { money, tier } = usePlanPrices()
  return (
    <div className={className}>
      <p className="flex flex-wrap items-baseline gap-x-2 text-white">
        <span className="type-display-sm-emphasized tabular-nums">
          {money(tier("once"))}
        </span>
        <span className="type-body-lg text-white/70">{PLAN.perYearShort}</span>
      </p>
      <p className="type-body-md mt-3 text-pretty text-white/70">
        {PLAN.moreOften(money(tier("twice")), money(tier("four")))}
      </p>
    </div>
  )
}

type PromiseItem = { text: string; icon: ReactNode }

/**
 * Three promises, each under its icon, as on the homepage card: white on
 * its photograph, or `dark` on a white card.
 */
function PromiseRow({
  items,
  tone = "light",
  className,
}: {
  items: PromiseItem[]
  tone?: "light" | "dark"
  className?: string
}) {
  return (
    <ul className={cn("flex gap-3 max-sm:flex-col max-sm:gap-0", className)}>
      {items.map(({ text, icon }) => (
        // A phone stacks the three, icon beside its line, so long German
        // and French lines do not wrap word by word (audit, 09.10).
        <li
          key={text}
          className="min-w-0 flex-1 py-2 max-sm:flex max-sm:items-center max-sm:gap-3 max-sm:py-0.5"
        >
          {/* On a phone the icon, 24px, is centred on the checklist's 20px
              column, so the marks share one axis and the lines start level
              (Eugene, 12.10). */}
          <span className="flex size-10 shrink-0 items-center max-sm:h-8 max-sm:w-5 max-sm:justify-center max-sm:[&>*]:size-6 max-sm:[&>*]:shrink-0">
            {icon}
          </span>
          <p
            className={cn(
              "type-body-md-emphasized mt-1 text-balance max-sm:mt-0",
              tone === "light" ? "text-white" : "text-neutral-900"
            )}
          >
            {text}
          </p>
        </li>
      ))}
    </ul>
  )
}

/** The plan's three promises, as on the homepage. */
function Promises({
  items,
  className,
}: {
  items: string[]
  className?: string
}) {
  return (
    <PromiseRow
      items={items.map((text, i) => {
        const Icon = PROMISE_ICONS[i]
        return { text, icon: <Icon className="size-7 text-white" /> }
      })}
      className={cn("px-[11px]", className)}
    />
  )
}

/**
 * The same promises as the homepage card shows them with the MRI on: the
 * doctor's video call, with her photograph, in the stethoscope's place. On
 * the MRI cards, in the plan card's form (Eugene, 30.09). The photograph is
 * the one today's pricing cards use for the call (mri-package-cards.tsx).
 */
export function MriPromises({
  PLAN,
  MRI,
  tone = "dark",
  blood = false,
  className,
}: {
  PLAN: Copy["PLAN"]
  MRI: Copy["MRI"]
  /** "light": white, for a photo (the MRI test page's phone hero). */
  tone?: "light" | "dark"
  /** The blood tests have no doctor's call, so the blood card's third
   *  promise, with its stethoscope, stands in its place (Eugene, 01.10). */
  blood?: boolean
  className?: string
}) {
  // Each mark about 28px where it shows, centred in the row's 40px box,
  // so all three sit the same distance above their text (Eugene, 30.09).
  // The shield fills its box; the target's ring is 22 of 28, so it gets a
  // bigger box; the photo is drawn at 28.
  const tint = tone === "light" ? "text-white" : "text-neutral-900"
  return (
    <PromiseRow
      tone={tone}
      items={[
        {
          text: PLAN.promises[0],
          icon: <ShieldPlusIcon className={cn("size-8", tint)} />,
        },
        {
          text: PLAN.promises[1],
          icon: <TargetIcon className={cn("size-9", tint)} />,
        },
        blood
          ? {
              text: PLAN.promises[2],
              icon: <StethoscopeFigmaIcon className={cn("size-7", tint)} />,
            }
          : {
              text: MRI.promiseCall,
              icon: (
                <Image
                  src={blobMediaUrl("/images/avatars/anna-erat.png")}
                  alt=""
                  width={40}
                  height={40}
                  className="size-7 rounded-full object-cover"
                />
              ),
            },
      ]}
      className={className}
    />
  )
}

/**
 * The plan's lines, checked, with plan prices lit. The list is as wide as
 * its longest line, so the lit row's pill ends near its text.
 */
function Checklist({
  items,
  className,
}: {
  items: string[]
  className?: string
}) {
  return (
    <ul className={cn("flex w-fit max-w-full flex-col gap-2", className)}>
      {items.map((item, i) => {
        const lit = i === PLAN_PRICES_LINE
        return (
          <li
            key={item}
            className="flex min-h-9 items-center gap-3 rounded-[18px] px-[11px] py-1.5"
          >
            {lit ? (
              <PercentSealIcon className="text-deep-green-400 size-[22px] shrink-0" />
            ) : (
              <CheckCircleNeutralIcon
                size={22}
                className="text-deep-green-400 shrink-0"
              />
            )}
            <span className="type-body-lg text-balance text-white">{item}</span>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * The plan's popup behind "Learn more": the booking's popup with the plan's
 * mark, its rhythms, all six lines and the blood test's markers from the CMS.
 * The homepage's baseline card opens it too, so the two cannot drift (the
 * site session, 30.09). It needs a PlanPricesProvider above it; without the
 * page's data it shows no marker list.
 */
export function PlanInfoSheet({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const { PLAN, INFO, PICK } = usePricingV5Copy()
  const { money, plan, biomarkersOf } = usePlanPrices()
  const read = useInfoContent()
  const content = read(PLAN_TEST, {
    name: PLAN.sheetName,
    picture: <PlanMark className="size-[92px] rounded-2xl sm:size-[131px]" />,
    meta: PLAN.meta.split(" · "),
    price: { now: money(plan), after: PLAN.perYear },
    description: PLAN.cardLede,
    // The blood test's own benefits read as a test's, not the plan's.
    keyBenefits: undefined,
    whoIsItFor: undefined,
    extra: <PlanRhythms PLAN={PLAN} />,
    whatYouGet: { heading: PLAN.includedHeading, items: PLAN.included },
    biomarkers: biomarkersOf(PLAN_TEST.cmsPackageId),
  })
  return (
    <InfoSheet
      content={content}
      open={open}
      onClose={onClose}
      labels={{ close: INFO.close, add: PICK.add, added: PICK.added }}
      tileCards={false}
    />
  )
}

/**
 * The Baseline plan's card. The booking button carries the scan picked so
 * far. "Learn more" opens the plan's popup. `largeNote` sets the line under
 * the card a step up, as the blood ad pages have it (Michèle, 21.10).
 */
export function BaselinePlanCard({
  copy,
  cta,
  track,
  largeNote = false,
}: {
  copy: Copy
  cta: { label: string; scan: BookingScan; addOnIds?: readonly string[] }
  track: string
  largeNote?: boolean
}) {
  const { PLAN, INFO } = copy
  const [open, setOpen] = useState(false)

  return (
    <HomeV4BaselineBackground>
      {/* Wider than the lists below it, 1120, with a bigger heading
          (Eugene, 30.09: "make it bigger, the card can be wider"). */}
      <div className="mx-auto max-w-[1120px]">
        <div className="background-study-card relative isolate overflow-hidden rounded-[32px] px-4 py-10 md:px-10 md:py-14 lg:px-12 lg:py-16">
          <div className="background-study-photo" aria-hidden="true" />
          {/* More air between the parts, top to bottom (Eugene, 30.09:
              "more vertical air, look how tight"). */}
          {/* The homepage card's layout as it is now (Eugene, 30.09): on the
              left the heading, the line under it, the price and the two
              buttons, the white one booking, the see-through one opening
              every benefit; the promises and lines on the right, 14px lower.
              A phone reads the left column first, as on the homepage. */}
          <div className="flex flex-col gap-12 lg:grid lg:grid-cols-2 lg:gap-x-12">
            <div>
              <h2 className="type-display-md-emphasized text-balance text-white">
                {PLAN.cardHeading}
              </h2>
              <p className="type-body-xl mt-6 text-pretty text-white/75">
                {PLAN.cardLede}
              </p>
              <YearPrice PLAN={PLAN} className="mt-10 md:mt-12" />
              <div className="mt-10 grid gap-3 sm:grid-cols-2 sm:gap-4 md:mt-12">
                <BookButton
                  scan={cta.scan}
                  addOnIds={cta.addOnIds}
                  label={cta.label}
                  trackingLabel="pricing_v5_plan_cta"
                  trackingLocation={track}
                  size="lg"
                  arrow={false}
                  className="block"
                  buttonClassName="w-full bg-white px-6 text-neutral-900 hover:bg-neutral-100 hover:shadow-none focus-visible:ring-white/60 md:px-6"
                />
                <Button
                  type="button"
                  size="lg"
                  variant="text"
                  aria-haspopup="dialog"
                  onClick={() => setOpen(true)}
                  className="w-full bg-white/20 px-6 text-white hover:bg-white/30 focus-visible:ring-white/60"
                >
                  {INFO.learnMore}
                </Button>
              </div>
            </div>
            <div className="lg:mt-[14px]">
              <Promises items={PLAN.promises} />
              <Checklist
                items={PLAN.included.slice(0, CARD_LINES)}
                className="mt-10"
              />
            </div>
          </div>
        </div>
        {/* Under the card, as in the booking's plan step: cancelling, and
            the one quiet way out to a one-time check-up (Eugene, 30.09). */}
        <div
          className={cn(
            largeNote ? "type-body-lg" : "type-body-md",
            "mt-6 flex flex-col items-center gap-1 text-center sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-3"
          )}
        >
          <p className="text-pretty text-neutral-600">{PLAN.cancelNote}</p>
          <OneTimeLink
            trackingLabel="pricing_v5_plan_one_off"
            trackingLocation={track}
            className="text-neutral-900 underline decoration-neutral-900/40 underline-offset-2 transition-colors hover:decoration-neutral-900"
          >
            {PLAN.oneOff}
          </OneTimeLink>
        </div>
      </div>
      <PlanInfoSheet open={open} onClose={() => setOpen(false)} />
    </HomeV4BaselineBackground>
  )
}
